// ============================================================
// CppAssignment.jsx
// ============================================================
// Junior-dev Notes:
// - Logic: Focuses on Pointers, Memory Management (RAII), and STL.
// - UI: Goldenrod accents (#E5C95F) on Neural Purple (#100720).
// - Strategy: 30 Challenges for Systems-Level Programming Mastery.
// - Branding: prodevconsult.com | Finnish Engineering Standards
// ============================================================

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    Chip,
} from "@mui/material";

/* Icons */
import { AiFillBulb } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import TerminalIcon from "@mui/icons-material/Terminal";
import MemoryIcon from "@mui/icons-material/Memory"; // For Low-level/Embedded feel
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1515879218367-8466d910aaa4";

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.92), rgba(16,7,32,0.96)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(255,255,255,0.03)",
    borderRadius: "16px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    border: "1px solid rgba(229, 201, 95, 0.15)",
    color: "white",
    transition: "0.3s ease",
    "&:hover": {
        border: "1px solid rgba(229, 201, 95, 0.4)",
        backgroundColor: "rgba(255,255,255,0.05)",
    }
}));

const QuestionItem = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    "&:last-child": { borderBottom: "none" },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#E5C95F",
    color: "#100720",
    fontWeight: 800,
    padding: "14px 45px",
    borderRadius: "4px",
    "&:hover": {
        backgroundColor: "#FFF0A5",
        transform: "translateY(-3px)",
    },
}));

/* ── Main Component ──────────────────────────────────────── */

export default function CppAssignment() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleCopy = (questions, index) => {
        const text = questions.join("\n");
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const assignmentData = useMemo(() => [
        {
            title: "Basics & Control Flow",
            questions: [
                "Implement a program that checks if a number is a Perfect Number.",
                "Create a Menu-driven program using 'switch' for bitwise operations (AND, OR, XOR).",
                "Generate the first N prime numbers using the Sieve of Eratosthenes.",
                "Calculate the sum of digits of a number using a 'while' loop.",
                "Use nested loops to print a Pascal Triangle pattern."
            ]
        },
        {
            title: "Pointers & Memory Management",
            questions: [
                "Swap two numbers using pointers (Pass-by-reference).",
                "Implement a dynamic array using 'new' and 'delete' keywords.",
                "Demonstrate Pointer Arithmetic to traverse and reverse an array.",
                "Explain the difference between a pointer and a reference with a code example.",
                "Create a 'Null-safe' pointer check and explain dangling pointers."
            ]
        },
        {
            title: "Object-Oriented Programming (OOP)",
            questions: [
                "Define a 'Box' class with Private members and use Constructor Overloading.",
                "Implement 'Multiple Inheritance' to model a SmartPhone (Camera + Phone + MusicPlayer).",
                "Showcase the 'Virtual' keyword for Runtime Polymorphism in a Shape hierarchy.",
                "Use 'Operator Overloading' to add two Complex Numbers (e.g., c1 + c2).",
                "Implement a Deep Copy vs Shallow Copy in a class with dynamic memory."
            ]
        },
        {
            title: "Data Structures & STL",
            questions: [
                "Sort a list of strings alphabetically using std::vector and std::sort.",
                "Implement a simple Stack using std::stack for Parentheses matching.",
                "Map country names to capitals using std::map and handle lookup errors.",
                "Remove duplicates from an integer vector using std::set.",
                "Use std::list to simulate a simple queue for task processing."
            ]
        },
        {
            title: "Advanced C++ & File I/O",
            questions: [
                "Implement a Template Function to find the maximum of any data type.",
                "Write a class that reads/writes student records to a binary file (.dat).",
                "Use Exception Handling (try-catch) to manage file opening failures.",
                "Demonstrate a Lambda expression to filter even numbers from a container.",
                "Implement a simple Smart Pointer (std::unique_ptr) to prevent memory leaks."
            ]
        },
        {
            title: "Algorithms & Logic",
            questions: [
                "Implement Binary Search on a sorted array of integers.",
                "Write a recursive function for the 'Towers of Hanoi' problem.",
                "Find the GCD (Greatest Common Divisor) using the Euclidean Algorithm.",
                "Implement a basic Selection Sort and count the number of swaps.",
                "Algorithm to find the 'Missing Number' in an array of 1 to N."
            ]
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 8, px: 2 }}>
                <Typography variant={isMobile ? "h4" : "h2"} sx={{ fontWeight: 900, color: "#E5C95F", mb: 1 }}>
                    C++ MASTER CHALLENGE
                </Typography>
                <Chip
                    label="PRODEV CONSULT · FINNISH ENGINEERING STANDARDS"
                    sx={{ bgcolor: "rgba(229, 201, 95, 0.1)", color: "#E5C95F", fontWeight: 800, border: "1px solid #E5C95F" }}
                />
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <MemoryIcon sx={{ fontSize: 40, color: "#E5C95F" }} />
                            <Typography variant="h5" fontWeight={800} color="#E5C95F">Systems Engineering Strategy</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.8, mb: 4, lineHeight: 1.8 }}>
                            C++ is the backbone of high-performance software. This master assignment challenges you to manage memory manually, optimize data structures with the STL, and master the **Standard Template Library**.
                        </Typography>

                        <Grid container spacing={2}>
                            {[
                                "Master Raw & Smart Pointers",
                                "Runtime Polymorphism & Virtual Tables",
                                "Standard Template Library (STL) Mastery",
                                "Manual Memory & RAII Principles",
                                "High-Efficiency Algorithms"
                            ].map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <PrecisionManufacturingIcon sx={{ color: "#00ED64", fontSize: 18 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>

                {assignmentData.map((topic, index) => (
                    <Grid item xs={12} key={index}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(229, 201, 95, 0.15)", color: "#E5C95F", border: "1px solid #E5C95F" }}>
                                        <TerminalIcon size={22} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={800}>{topic.title}</Typography>
                                </Box>

                                <Stack direction="row" spacing={1.5}>
                                    <Tooltip title={copiedIndex === index ? "Copied!" : "Copy Tasks"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(topic.questions, index)}
                                            sx={{ color: "white", borderColor: "rgba(255,255,255,0.2)" }}
                                        >
                                            {copiedIndex === index ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<CodeIcon />}
                                            sx={{ bgcolor: "#E5C95F", color: "#100720", fontWeight: 700 }}
                                        >
                                            CPP Compiler
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                {topic.questions.map((q, i) => (
                                    <QuestionItem key={i}>
                                        <Typography sx={{ color: "#E5C95F", fontWeight: 900, minWidth: "25px" }}>{i + 1}.</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.85 }}>{q}</Typography>
                                    </QuestionItem>
                                ))}
                            </Box>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 6, mb: 10 }}>
                    <Stack direction={isMobile ? "column" : "row"} spacing={3} justifyContent="center" alignItems="center">
                        <Link to="/cpp-dashboard" style={{ textDecoration: "none" }}>
                            <ActionButton variant="contained">
                                BACK TO DASHBOARD
                            </ActionButton>
                        </Link>
                    </Stack>
                </Grid>
            </Grid>

            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    sx={{ position: "fixed", bottom: 30, right: 30, bgcolor: "#E5C95F" }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
